import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  Box,
  Typography,
  Button,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  IconButton,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import { useAuth } from "../../../context/AuthContext.jsx";

const COLORS = {
  fucsia: "#F21C63",
  purple: "#6C4DE2",
  dark: "#211E26",
};

const CART_API = "https://apiweb.hitpoly.com/ajax/carritoController.php";
const TRIALS_API = "https://apiweb.hitpoly.com/ajax/trialsController.php";

const PLANES = [
  {
    id: "profesional",
    nombre: "Sistema Todo en Uno Profesionales",
    precio: "$19.90",
    precioNum: 19.9,
    periodo: "/ mes",
    slug: "sistema-todo-en-uno-profesionales",
    descripcion:
      "Plataforma todo en uno para profesionales: academia, bolsa de trabajo y CRM con acceso a la comunidad.",
    image: "https://ik.imagekit.io/wfm4vebzr/holding/services/profesionales.jpg",
    beneficios: [
      "Academia comunidad",
      "Bolsa de trabajo",
      "CRM Pro",
      "Herramientas IA",
    ],
    destacado: true,
    badge: "PROFESIONAL",
  },
  {
    id: "empresa",
    nombre: "Sistema Todo en Uno Empresa",
    precio: "$29",
    precioNum: 29,
    periodo: "/ mes",
    slug: "sistema-todo-en-uno-empresa",
    descripcion:
      "Plataforma todo en uno para empresas: academia, bolsa de trabajo, CRM con acceso a la comunidad y cuentas de equipo.",
    image: "https://ik.imagekit.io/wfm4vebzr/holding/services/empresa.jpg",
    beneficios: [
      "Academia comunidad",
      "Bolsa de trabajo",
      "CRM Pro",
      "Cuentas de equipo",
      "Herramientas IA",
    ],
    destacado: false,
    badge: "EMPRESA",
  },
];

const ProPlanModal = ({ open, onClose, herramienta, onTrialStarted, vencido }) => {
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    console.log("[POPUP] Cambio en open ->", open, "| herramienta:", herramienta || "(ninguna)", "| vencido:", !!vencido);
  }, [open, herramienta, vencido]);

  const startTrial = async () => {
    const currentUserId = user?.id || user?.user_id;
    if (!currentUserId) {
      alert("Debes iniciar sesión para probar Hitpoly Pro.");
      if (onClose) onClose();
      return;
    }
    try {
      const resp = await fetch(TRIALS_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          accion: "iniciarPrueba",
          usuario_id: currentUserId,
          plan: "profesional",
        }),
      });
      const text = await resp.text();
      let json = null;
      try {
        json = JSON.parse(text);
      } catch (parseErr) {
        json = null;
      }

      if (json && json.status === "success") {
        try {
          localStorage.setItem(
            "hitpoly_trial",
            JSON.stringify({ fin: json.fin, origen: "prueba" })
          );
        } catch (e) {}
        window.dispatchEvent(new CustomEvent("pro-trial-started"));
        if (onTrialStarted) onTrialStarted(json.fin);
        if (onClose) onClose();
      } else {
        alert((json && json.message) || "No se pudo iniciar la prueba gratis.");
      }
    } catch (e) {
      alert("Ocurrió un error al iniciar la prueba gratis: " + (e && e.message ? e.message : e));
    }
  };

  const addPlanToCart = async (plan) => {
    const currentUserId = user?.id || user?.user_id;
    if (!currentUserId) {
      alert("Debes iniciar sesión para adquirir Hitpoly Pro.");
      if (onClose) onClose();
      return;
    }

    const product = {
      id: `pro_${plan.id}`,
      type: "pro",
      name: `Hitpoly Pro ${plan.nombre}`,
      details: plan.descripcion,
      image: "",
      price_original: plan.precioNum,
      price_final: plan.precioNum,
      origen: "pro",
      tipoContrato: plan.nombre,
      servicio_nombre: "Hitpoly Pro",
      beneficios: plan.beneficios || [],
    };

    try {
      const resp = await fetch(CART_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          accion: "addItem",
          usuario_id: currentUserId,
          producto_id: String(product.id).replace("serv_", ""),
          tipo: product.type,
          nombre: product.name,
          descripcion: product.details,
          imagen: product.image,
          precio_original: product.price_original,
          precio_final: product.price_final,
          origen: product.origen,
          tipo_contrato: product.tipoContrato,
          servicio_nombre: product.servicio_nombre,
          tipo_servicio: "",
          beneficios: product.beneficios.length
            ? JSON.stringify(product.beneficios)
            : "",
        }),
      });
      const json = await resp.json();
      if (json.status === "success" || json.status === "info") {
        if (json.status === "info") {
          alert(json.message || "Este producto ya está en tu carrito.");
        }
        window.dispatchEvent(new CustomEvent("open-cart-drawer"));
      } else {
        alert(json.message || "No se pudo añadir al carrito.");
      }
    } catch (e) {
      alert("Ocurrió un error al añadir el plan al carrito.");
    } finally {
      if (onClose) onClose();
    }
  };

  const navigate = useNavigate();
  // IDs reales de los servicios Pro en la Bolsa de Profesionales (routes.jsx)
  // Profesional: servicio-profesional/11 (slug: sistema-todo-en-uno-profesionales)
  // Empresa: servicio-empresa/12 (slug: sistema-todo-en-uno-empresa)
  const irAlServicio = (plan) => {
    const currentUserId = user?.id || user?.user_id || 1500;
    const slug = plan.slug || plan.id || "";
    let url = "/servicios";
    if (String(slug).includes("empresa")) {
      url = `/systems/bolsa/servicio-empresa/12?userId=${currentUserId}`;
    } else if (String(slug).includes("profesional")) {
      url = `/systems/bolsa/servicio-profesional/11?userId=${currentUserId}`;
    }
    console.log("[POPUP] Click servicio popup -> url:", url, "| plan:", plan.id, "| location antes:", window.location.pathname + window.location.search);
    const navMatch = url.match(/^\/systems\/([^/?]+)/);
    if (navMatch) {
      sessionStorage.setItem("hitpoly_popup_nav", JSON.stringify({ sys: navMatch[1], t: Date.now() }));
      console.log("[POPUP] Supresor de reapertura seteado para sistema:", navMatch[1]);
    }
    if (onClose) {
      console.log("[POPUP] Ejecutando onClose() del popup");
      onClose();
    }
    navigate(url);
    console.log("[POPUP] navigate() ejecutado | location despues:", window.location.pathname + window.location.search);
    setTimeout(() => {
      console.log("[POPUP] location a los 500ms:", window.location.pathname + window.location.search);
    }, 500);
    setTimeout(() => {
      console.log("[POPUP] location a los 2000ms:", window.location.pathname + window.location.search);
    }, 2000);
  };

  return (
    <Dialog
      open={open}
      onClose={() => {
        console.log("[POPUP] Dialog onClose disparado (ESC o click fuera)");
        if (onClose) onClose();
      }}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      sx={{ zIndex: 130000 }}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : "18px",
          overflow: "hidden",
          height: isMobile ? "100vh" : "auto",
          maxHeight: isMobile ? "100vh" : "90vh",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* CONTENEDOR SCROLLABLE COMPLETO */}
      <Box sx={{
        flexGrow: 1,
        overflow: "auto",
        scrollbarWidth: "thin",
        scrollbarColor: `${COLORS.purple} transparent`,
        minHeight: 0,
        "&::-webkit-scrollbar": { width: 6 },
        "&::-webkit-scrollbar-track": { background: "transparent" },
        "&::-webkit-scrollbar-thumb": { backgroundColor: COLORS.purple, borderRadius: 3 },
        "&::-webkit-scrollbar-thumb:hover": { backgroundColor: COLORS.fucsia },
      }}>
        {/* ENCABEZADO: título + subtítulo de la oferta */}
        <IconButton
          onClick={() => {
            console.log("[POPUP] Click en X cerrar popup");
            if (onClose) onClose();
          }}
          sx={{ position: "absolute", top: 20, right: 24, zIndex: 10, color: "#fff", bgcolor: "rgba(255,255,255,0.15)", "&:hover": { bgcolor: "rgba(255,255,255,0.25)" } }}
          aria-label="Cerrar"
        >
          <CloseIcon />
        </IconButton>
        <Box
        sx={{
          width: "100%",
          background: `linear-gradient(135deg, ${COLORS.purple} 0%, ${COLORS.fucsia} 100%)`,
          color: "#fff",
          py: 4,
          px: { xs: 3, md: 6 },
          textAlign: "center",
        }}
      >
        <Typography variant="overline" sx={{ letterSpacing: 2, opacity: 0.85 }}>
          HITPOLY PRO
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
          Hazte Hitpoly Pro y desbloquea todo
        </Typography>
        <Typography variant="body1" sx={{ opacity: 0.92, maxWidth: "620px", mx: "auto", mb: 2 }}>
          {herramienta
            ? `La herramienta "${herramienta}" es exclusiva de la versión Pro. Elige el plan que prefieras y llévate acceso total a comunidad, academia, bolsa, herramientas IA y CRM.`
            : "Lleva tu negocio al siguiente nivel con acceso total a la comunidad, academia, bolsa de profesionales, herramientas IA y CRM. Elige el plan que mejor se adapte a ti."}
        </Typography>
        {!vencido && (
          <Button
            variant="contained"
            onClick={startTrial}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              color: "#fff",
              backgroundColor: COLORS.fucsia,
              borderRadius: "30px",
              px: 4,
              "&:hover": { backgroundColor: "#d8155a" },
            }}
          >
            Probar Hitpoly Free 3 días
          </Button>
        )}
        {vencido && (
          <Box
            sx={{
              textAlign: "center",
              mt: 1,
              mx: "auto",
              width: "100%",
              maxWidth: "620px",
              px: 3,
              py: 2,
              borderRadius: "30px",
              backgroundColor: "#fff",
              border: `1.5px solid ${COLORS.fucsia}`,
            }}
          >
            <Typography variant="body2" sx={{ color: COLORS.fucsia, fontWeight: 800, whiteSpace: "normal", overflow: "visible", wordBreak: "break-word", fontSize: { xs: "0.85rem", sm: "1rem" } }}>
              Tu prueba gratuita ya venció. Para continuar necesitas adquirir un plan.
            </Typography>
          </Box>
        )}
      </Box>

      {/* CONTENIDO */}
      <Box sx={{ flex: 1, minHeight: 0 }}>
        {/* DOS PLANES */}
        <Box sx={{ py: 4, px: { xs: 2, md: 4 } }}>
          <Grid container spacing={3} justifyContent="center">
          {PLANES.map((plan) => (
            <Grid item xs={12} sm={6} key={plan.id}>
              <Box
                sx={{
                  position: "relative",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  borderRadius: "14px",
                  border: plan.destacado
                    ? `2px solid ${COLORS.fucsia}`
                    : "1px solid #e3e3e3",
                  p: 3,
                  textAlign: "center",
                  backgroundColor: plan.destacado ? "#fff7fb" : "#fff",
                  boxShadow: plan.destacado
                    ? "0 10px 26px rgba(242,28,99,0.18)"
                    : "0 4px 12px rgba(0,0,0,0.05)",
                }}
              >
                {plan.badge && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: -10,
                      left: "50%",
                      transform: "translateX(-50%)",
                      backgroundColor: COLORS.fucsia,
                      color: "#fff",
                      fontSize: "10px",
                      fontWeight: 800,
                      letterSpacing: 1,
                      px: 1.5,
                      py: 0.3,
                      borderRadius: "20px",
                    }}
                  >
                    {plan.badge}
                  </Box>
                )}

                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ color: "#666", lineHeight: 1.45, textAlign: "left" }}>
                    {plan.descripcion}
                  </Typography>
                </Box>

                <Box sx={{ textAlign: "left", mb: 3, flexGrow: 1 }}>
                  <List dense disablePadding>
                    {plan.beneficios.map((b, i) => (
                      <ListItem key={i} sx={{ py: 0.3 }}>
                        <ListItemIcon sx={{ minWidth: 24 }}>
                          <CheckCircleIcon
                            style={{ color: COLORS.purple, fontSize: 18 }}
                          />
                        </ListItemIcon>
                        <Typography variant="body2" sx={{ color: "#555" }}>
                          {b}
                        </Typography>
                      </ListItem>
                    ))}
                  </List>
                </Box>

                <Button
                  variant={plan.destacado ? "contained" : "outlined"}
                  onClick={() => irAlServicio(plan)}
                  sx={{
                    mt: "auto",
                    borderRadius: "30px",
                    textTransform: "none",
                    fontWeight: 700,
                    backgroundColor: plan.destacado
                      ? COLORS.fucsia
                      : "transparent",
                    color: plan.destacado ? "#fff" : COLORS.purple,
                    borderColor: plan.destacado
                      ? COLORS.fucsia
                      : COLORS.purple,
                    "&:hover": {
                      backgroundColor: plan.destacado
                        ? "#d8155a"
                        : "rgba(108,77,226,0.08)",
                    },
                  }}
                >
                  Ver más y precios — {plan.precio} USD
                </Button>
              </Box>
            </Grid>
          ))}
        </Grid>
        </Box>
      </Box>
      </Box>
    </Dialog>
  );
};

export default ProPlanModal;
